if (!customElements.get('product-add-to-cart-sticky')) {
  class ProductAddToCartSticky extends HTMLElement {
    constructor() {
      super();
      this.sectionId = this.dataset.section;
      this.handleScroll = this.handleScroll.bind(this);
      this.handleVariantChange = this.handleVariantChange.bind(this);
      this.handleStickyClick = this.handleStickyClick.bind(this);
      this.syncButtonState = this.syncButtonState.bind(this);
      this.handleCartError = this.handleCartError.bind(this);
    }

    connectedCallback() {
      this.section = document.getElementById(`MainProduct-${this.sectionId}`);
      this.mainForm = document.getElementById(this.dataset.productId);
      this.mainButton = this.section?.querySelector(`#ProductSubmitButton-${CSS.escape(this.sectionId)}`);
      this.stickyButton = this.querySelector(`#StickyProductSubmitButton-${CSS.escape(this.sectionId)}`);
      this.footerElement = document.querySelector('footer');
      this.statusElement = this.querySelector('[data-aif-sticky-status]');
      this.mobileMedia = window.matchMedia('(max-width: 767px)');

      if (!this.section || !this.mainForm || !this.mainButton || !this.stickyButton) return;

      this.stickyButton.addEventListener('click', this.handleStickyClick);
      window.addEventListener('scroll', this.handleScroll, { passive: true });
      window.addEventListener('resize', this.handleScroll, { passive: true });
      document.body.addEventListener('change', this.handleVariantChange);

      this.buttonObserver = new MutationObserver(this.syncButtonState);
      this.buttonObserver.observe(this.mainButton, {
        attributes: true,
        attributeFilter: ['disabled', 'aria-disabled', 'class'],
        childList: true,
        subtree: true,
      });

      this.readinessObserver = new MutationObserver(this.handleScroll);
      this.readinessObserver.observe(document.body, {
        attributes: true,
        attributeFilter: ['class'],
      });

      this.sectionObserver = new MutationObserver(this.handleScroll);
      this.sectionObserver.observe(this.section, { childList: true, subtree: true });

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.cartErrorUnsubscriber = subscribe(PUB_SUB_EVENTS.cartError, this.handleCartError);
      }

      this.syncButtonState();
      this.handleScroll();
    }

    disconnectedCallback() {
      this.stickyButton?.removeEventListener('click', this.handleStickyClick);
      window.removeEventListener('scroll', this.handleScroll);
      window.removeEventListener('resize', this.handleScroll);
      document.body.removeEventListener('change', this.handleVariantChange);
      this.buttonObserver?.disconnect();
      this.readinessObserver?.disconnect();
      this.sectionObserver?.disconnect();
      this.cartErrorUnsubscriber?.();
    }

    isCloudliftReady() {
      return document.body.classList.contains('cl-app-loaded');
    }

    handleScroll() {
      if (!this.mainButton) return;

      const buttonRect = this.mainButton.getBoundingClientRect();
      const footerRect = this.footerElement?.getBoundingClientRect();
      const footerIsVisible = footerRect ? footerRect.top < window.innerHeight : false;
      const mainButtonIsVisible = buttonRect.bottom > 0 && buttonRect.top < window.innerHeight;
      const upsellContainer = this.section.querySelector('[data-essential-upsell-element="container"]');
      const upsellRect = upsellContainer?.getBoundingClientRect();
      const upsellIsVisible = upsellRect
        ? upsellRect.bottom > 0 && upsellRect.top < window.innerHeight
        : false;
      const shouldShowOnMobile = this.mobileMedia.matches && !mainButtonIsVisible;
      const shouldShowOnDesktop = !this.mobileMedia.matches && buttonRect.bottom <= 0;
      const shouldShow = (shouldShowOnMobile || shouldShowOnDesktop)
        && !footerIsVisible
        && !upsellIsVisible
        && this.isCloudliftReady();

      this.classList.toggle('show', shouldShow);
      this.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
    }

    syncButtonState() {
      if (!this.mainButton || !this.stickyButton) return;

      const disabled = this.mainButton.disabled || this.mainButton.getAttribute('aria-disabled') === 'true';
      const mainText = this.mainButton.querySelector('span')?.textContent?.trim();
      const stickyText = this.stickyButton.querySelector('span');
      const stickySpinner = this.stickyButton.querySelector('.loading__spinner');
      const isLoading = this.mainButton.classList.contains('loading');

      this.stickyButton.disabled = disabled;
      this.stickyButton.setAttribute('aria-disabled', disabled ? 'true' : 'false');
      this.stickyButton.classList.toggle('loading', isLoading);
      stickySpinner?.classList.toggle('hidden', !isLoading);

      if (mainText && stickyText) stickyText.textContent = mainText;
    }

    handleStickyClick(event) {
      event.preventDefault();
      this.announce('');

      if (!this.isCloudliftReady()) {
        this.announce('Product options are still loading. Please try again in a moment.');
        return;
      }

      const invalidField = this.mainForm.querySelector(':invalid')
        || this.section.querySelector('.cl-po--input:invalid');
      if (invalidField) {
        this.revealInvalidField(invalidField);
        return;
      }

      if (this.mainButton.disabled || this.mainButton.getAttribute('aria-disabled') === 'true') return;
      this.mainButton.click();
    }

    handleCartError(event) {
      if (event?.source !== 'product-form') return;

      const currentVariantId = this.mainForm.querySelector('[name="id"]')?.value;
      if (event.productVariantId && String(event.productVariantId) !== String(currentVariantId)) return;

      this.announce(event.message || event.errors || 'This item could not be added. Please try again.');
    }

    revealInvalidField(field) {
      const fieldContainer = field.closest('.cl-po--wrapper, .cl-po--option, .product-form__input') || field;
      this.section.querySelectorAll('.aif-has-error').forEach((element) => element.classList.remove('aif-has-error'));
      fieldContainer.classList.add('aif-has-error');
      fieldContainer.scrollIntoView({ behavior: 'smooth', block: 'center' });
      this.announce('Please choose the required product options before adding this item.');

      window.setTimeout(() => {
        try {
          field.focus({ preventScroll: true });
          field.reportValidity();
        } catch (error) {
          field.focus();
        }
      }, 350);
    }

    announce(message) {
      if (!this.statusElement) return;
      this.statusElement.textContent = '';
      if (!message) return;
      window.requestAnimationFrame(() => {
        this.statusElement.textContent = message;
      });
    }

    updateStickyATCOption(optionName, optionValue) {
      if (!optionName) return;
      const matches = optionName.match(/options\[(.+)\]/);
      if (matches?.[1]) optionName = matches[1];

      const optionDisplay = this.querySelector(
        `.selected-variant-option[data-option-name="${CSS.escape(optionName)}"]`
      );
      if (optionDisplay) optionDisplay.textContent = `${optionName}: ${optionValue}`;
    }

    handleVariantChange(event) {
      const target = event.target;
      if (!target.closest(`#MainProduct-${CSS.escape(this.sectionId)}`)) return;
      if (!['INPUT', 'SELECT'].includes(target.tagName)) return;

      const optionValue = target.tagName === 'SELECT'
        ? target.options[target.selectedIndex]?.textContent?.trim()
        : target.value;
      this.updateStickyATCOption(target.name, optionValue);
      this.section.querySelectorAll('.aif-has-error').forEach((element) => element.classList.remove('aif-has-error'));
    }
  }

  customElements.define('product-add-to-cart-sticky', ProductAddToCartSticky);
}
